import { useState, useEffect, useCallback } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Autocomplete, Box, Button, LinearProgress, TextField, Typography, TableCell, TableRow, TableBody, Table, TableContainer, Paper, InputLabel, Select, FormControl, MenuItem, SelectChangeEvent, Tooltip } from '@mui/material';
import { Close, Search } from '@mui/icons-material';
import { DataGridPro, DataGridProProps } from '@mui/x-data-grid-pro';
import { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import useAuth from '../context/AuthProvider';
import { apiClient } from '../helper/api';
import { round } from 'lodash';
import { initPartner, PartnerType } from '../models/PartnersModel';
// import { PieChart } from '@mui/icons-material';
import { PieChart } from '@mui/x-charts/PieChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { LightBlueBtnColor, HoverUnselectedLightBlueBtnColor, WhiteColor, brandColors, neutralColors } from '../helper/theme';
import { Months } from '../helper/utils';
import { Grid } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const PIE_CHART_COLORS = [
    brandColors.royalBlue,
    brandColors.navyBlue,
    brandColors.blue100,
    brandColors.orangeCustom,
    brandColors.purpleCustom,
    brandColors.green,
    brandColors.red,
    '#5c6bc0',
    '#26a69a',
];

const getMonthName = (month: number) => { return Months[month as keyof typeof Months]; };

export interface MonthlyAccountPerformanceReportModel {
    month: number;
    year: number;
    partnersId: number;
};

/* The metrics will be:
1. Total Transactions Reported to MISC during month
    - These will be the transaction reported and approved via OTR into the MISC system within one calendar month
2. # of Filings Processed without Return
    - These will be transactions that are reported and marked Filed without any "I Want To...Report a Tag"s initiated
3. # of Filings Returned with Errors
    - These will be transactions that have a "I Want To...Report a Tag"s initiated
4. Error Breakdown by Type and Frequency
    - This will be a log of the Error Codes from the attached 
    - Since reporting Multiple tags at once lump all the codes in one sum, we need to implement an OCR type function here to read the uploaded tag document for the error and map to the appropriate codes on the attached
5. Timeliness of document submission
    - Average turnaround time from status Waiting on Client to status Filed 
6. Client Response Times
    - Average time it takes a client to reply to a message sent by an MISC user
*/

export interface MonthlyAccountPerformanceReportData {
    // 1. Total Transactions Reported to MISC during month
    totalTransactionsReported: number;
    // 2. # of Filings Processed without Return
    // totalFilingsProcessed: number;
    totalFilingsProcessed: {
        policyStatus: string;
        totalFilingsProcessed: number;
    }[];
    // 3. # of Filings Returned with Errors
    totalFilingsReturnedWithErrors: number;
    // transactions returned to the client "Waiting on Client"
    transactionsReturnedToClient: number;
    // 4. Error Breakdown by Type and Frequency
    errorBreakdownByTypeAndFrequency: ErrorBreakdownModel[];
    // 5. Average # of Days to attach documents
    avgAttachDocumentsDays: number;
    // 6. Average # of Days to respond to Returned Filings
    clientResponseTimes: {
        avgResponseTimeDays: number;
    }
    // 7. Filings by month
    filingsByMonth: FilingsByMonthModel[];
    // error breakdown by state and policy type
    errorBreakdownByStateAndPolicyType: ErrorBreakdownByStateAndPolicyTypeModel[];
};
export interface FilingsByMonthModel {
    month: number;
    year: number;
    totalTransactions: number;
    totalFilingsWithErrors: number;
    errorReturnRate: number;
};
export interface ErrorBreakdownModel {
    errorTagCodeId: number;
    errorTagName: string;
    errorsReported: number;
    percentageOfTotalReports: number;
};

//#region error breakdown
export interface ErrorBreakdownByStateAndPolicyTypeModel{
    policyNumber: string;
    stateId: number;
    stateAbbv: string;
    transactionTypeId: number;
    transactionType: string;  // transaction type name
    errorTagCodeId: number;
    errorTagName: string;
    errorsReported: number;
    percentageOfStatePolicyTotal: number;
};
export type FilterableField = 'state' | 'transactionType';
export interface FilterConfig {
    field: FilterableField;
    value?: string | number; // Optional filter value
};
export interface ErrorTagBreakdown {
    errorTagName: string;
    errorsReported: number;
    percentage: number;
};
export interface GroupedErrorBreakdown {
    groupKey: string;
    groupLabel: string;
    policyCount: number;
    totalErrors: number;
    breakdown: ErrorTagBreakdown[];
    items: ErrorBreakdownByStateAndPolicyTypeModel[];
};
export interface ErrorBreakdownTableRow {
    id: string;
    groupLabel: string;
    policyCount: number;
    totalErrors: number;
    breakdown: {
        errorTagName: string;
        errorsReported: number;
        percentage: number;
    }[];
    breakdownSummary: string;
};
//#endregion

export const MonthlyAccountPerformanceReport = () => {
    const { user } = useAuth();
    const token = user?.token;
    const RedColor = '#d32f2f';
    const IsClientUser = user?.userRoles.split(",").includes("Client Admin");
    const [loading, setLoading] = useState(false);
    const [partners, setPartners] = useState<PartnerType[]>([initPartner]);
    // const CurrentYear = new Date().getFullYear();
    const [formData, setFormData] = useState<MonthlyAccountPerformanceReportModel>({
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        partnersId: IsClientUser ? user?.partnersId || 0 : 0,
    });
    /** Month/year shown on metrics & charts — updated when Search/Clear completes, not when dropdowns change */
    const [reportedPeriod, setReportedPeriod] = useState<{ month: number; year: number }>({
        month: formData.month,
        year: formData.year,
    });
    const [performanceReportData, setPerformanceReportData] = useState<MonthlyAccountPerformanceReportData>({
        totalTransactionsReported: 0,
        totalFilingsProcessed: [],
        totalFilingsReturnedWithErrors: 0,
        transactionsReturnedToClient: 0,
        errorBreakdownByTypeAndFrequency: [],
        avgAttachDocumentsDays: 0,
        clientResponseTimes: {
            // avgResponseTimeHours: 0,
            avgResponseTimeDays: 0,
        },
        filingsByMonth: [],
        errorBreakdownByStateAndPolicyType: [],
    });

    //#region API Calling
    const getPartners = () => {
        setLoading(true);
        apiClient(`/Partners/getPartners`, {
            method: 'GET',
            headers: new Headers({
                Authorization: `Bearer ${token}`,
            }),
        }).then(response => {
            if (response.status) {
                setPartners(response.data);
            }
        }).finally(() => {
            setLoading(false);
        });
    };
    const getMonthlyAccountPerformanceReport = (overrides?: Partial<MonthlyAccountPerformanceReportModel>) => {
        const params: MonthlyAccountPerformanceReportModel = { ...formData, ...overrides };
        setLoading(true);
        apiClient(`/getMonthlyAccountPerformanceReport?month=${params.month}&year=${params.year}&partnersId=${params.partnersId}`, {
            method: 'GET',
            headers: new Headers({
                Authorization: `Bearer ${token}`,
            }),
        }).then(response => {
            setPerformanceReportData(response.data);
            setReportedPeriod({ month: params.month, year: params.year });
        }).finally(() => {
            setLoading(false);
        });
    };

    useEffect(() => {
        getPartners();
        getMonthlyAccountPerformanceReport();
    // }, [formData]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    //#endregion API Calling

    const handleChange = (e: SelectChangeEvent<number>) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const borderColor = '#e2e8f0';
    const selectSx = {
        '& .MuiOutlinedInput-notchedOutline': { borderColor },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: LightBlueBtnColor },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: LightBlueBtnColor + ' !important', borderWidth: '1px' },
    };

    const MonthDropdown = () => (
        <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="month-select-label">Month</InputLabel>
            <Select value={formData.month} name="month" 
                labelId="month-select-label" 
                label="Month"
                onChange={handleChange}
                sx={{ height: '2.75rem', ...selectSx }}
            >
                <MenuItem value={0}>Please select Month </MenuItem>
                {Object.keys(Months).map((month) => (
                    <MenuItem key={month} value={parseInt(month)}>{Months[parseInt(month) as keyof typeof Months]}</MenuItem>
                ))}
            </Select>
        </FormControl>
    );
    const YearDropdown = () => (
        <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel id="year-select-label">Year</InputLabel>
            <Select value={formData.year} name="year"
                labelId="year-select-label"
                onChange={handleChange}
                label="Year"
                sx={{ height: '2.75rem', ...selectSx }}
            >
                {/* past 10 years */}
                {Array.from({ length: 10 }, (_, i) => {
                    const year = new Date().getFullYear() - i;
                    return (
                        <MenuItem key={year} value={year}>{year}</MenuItem>
                    );
                })}
            </Select>
        </FormControl>
    );
    const clientOptions: { partnersId: number; customerName: string }[] = [
        { partnersId: 0, customerName: 'All Clients' },
        ...partners.filter((p) => p.partnersId !== 0).map((p) => ({ partnersId: p.partnersId, customerName: p.customerName ?? '' })),
    ];
    const PartnerDropdown = () => (
        <Autocomplete
            options={clientOptions}
            getOptionLabel={(option) => option.customerName}
            value={clientOptions.find((opt) => opt.partnersId === formData.partnersId) ?? null}
            onChange={(_e, newValue) => setFormData({ ...formData, partnersId: newValue?.partnersId ?? 0 })}
            isOptionEqualToValue={(option, value) => option.partnersId === value.partnersId}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Client"
                    size="small"
                    sx={{
                        minWidth: 280,
                        '& .MuiOutlinedInput-root': { height: '2.75rem' },
                        ...selectSx,
                    }}
                />
            )}
        />
    );

    /** Flat modern styling aligned with site design */
    const pageHeaderBg = '#fafbfc';
    const tableHeaderBg = '#fafbfc';
    const tableRowAltBg = '#f8fafc';
    const textPrimary = '#0f172a';
    const textMuted = '#64748b';
    const borderColorFlat = '#e2e8f0';

    const BoxTitleStyle = {
        fontWeight: 600,
        fontSize: '0.9375rem',
        color: textPrimary,
        width: '100%',
        padding: '12px 16px',
        borderBottom: `1px solid ${borderColorFlat}`,
        backgroundColor: WhiteColor,
    };
    const TableCellDescriptionStyle = {
        fontWeight: 500,
        backgroundColor: tableHeaderBg,
        color: textPrimary,
        width: '400px',
        borderBottom: `1px solid ${borderColorFlat}`,
    };
    const TableCellValueStyle = {
        fontWeight: 500,
        backgroundColor: WhiteColor,
        color: textPrimary,
        borderBottom: `1px solid ${borderColorFlat}`,
    };
    const TableContainerStyle = (maxWidth: number | null = null) => ({
        ...(maxWidth && { maxWidth: maxWidth }),
        display: 'inline-block',
        borderRadius: '8px',
        overflow: 'hidden',
        border: `1px solid ${borderColorFlat}`,
        boxShadow: 'none',
    });
    const TableStyle = {
        borderCollapse: 'collapse',
        '& td': {
            borderBottom: `1px solid ${borderColorFlat}`,
            padding: '10px 14px',
            textAlignLast: 'start',
        },
    };

    const getPieChartData = () => {
        return performanceReportData.errorBreakdownByTypeAndFrequency.filter(error => error.errorsReported > 0).map((error, index) => {
            return {
                id: error.errorTagCodeId,
                value: error.errorsReported,
                label: `${error.errorTagCodeId}. ${error.errorTagName}`,
                color: PIE_CHART_COLORS[index % PIE_CHART_COLORS.length],
            };
        });
    };
    const getErrorTypeColor = useCallback((errorTagName: string) => {
        const index = performanceReportData.errorBreakdownByTypeAndFrequency.findIndex(
            (error) => error.errorTagName === errorTagName
        );
        if (index === -1) {
            return PIE_CHART_COLORS[0];
        }
        return PIE_CHART_COLORS[index % PIE_CHART_COLORS.length];
    }, [performanceReportData.errorBreakdownByTypeAndFrequency]);
    const getChartData = () => {
        return Object.keys(Months).map((month) => {
            const monthNumber = parseInt(month);
            
            const filing = performanceReportData.filingsByMonth
                .filter(f => f.year === reportedPeriod.year)
                .find(f => f.month === monthNumber);
            const totalTransactions = filing?.totalTransactions || 0;
            const totalFilingsWithErrors = filing?.totalFilingsWithErrors || 0;
            const errorPercent = totalTransactions > 0
                ? round((totalFilingsWithErrors / totalTransactions) * 100, 0)
                : 0;
            const nonErrorPercent = totalTransactions > 0 ? 100 - errorPercent : 0;

            return {
                month: Months[monthNumber as keyof typeof Months].slice(0, 3).toUpperCase(),
                totalTransactions,
                totalFilingsWithErrors,
                errorPercent,
                nonErrorPercent,
            };
        });
    };
    const chartData = getChartData();
    const hasFilingsByMonthData = chartData.some((filing) =>
        filing.totalTransactions > 0 || filing.totalFilingsWithErrors > 0
    );
    const columnOptions = {
        sortable: false,
        filterable: false,
    };
    const errorTypeColumns: GridColDef[] = [
        { field: 'errorType', headerName: 'Error Type',
            flex: 1,
            minWidth: 260,
            ...columnOptions,
            renderCell: (params: GridRenderCellParams) => {
                if(!params.row.errorTagName){return params.value;}
                return (
                    <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Box sx={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '2px',
                            backgroundColor: getErrorTypeColor(params.row.errorTagName),
                            flexShrink: 0,
                        }} />
                        <Box sx={{ flex: 1 }}>{params.value}</Box>
                    </Box>
                );
            },
        },
        { field: 'errorsReported', headerName: 'Errors Reported',
            type: 'number',
            width: 160,
            align: 'center',
            headerAlign: 'center',
            ...columnOptions,
        },
        { field: 'percentageOfTotalReports', headerName: '% of Total',
            type: 'number',
            width: 140,
            align: 'left',
            headerAlign: 'left',
            valueFormatter: (params) => `${params.value}%`,
            ...columnOptions,
        },
    ];
    const hasErrorTypeData = performanceReportData.errorBreakdownByTypeAndFrequency.some(error => error.errorsReported > 0);
    const errorTypeRows = performanceReportData.errorBreakdownByTypeAndFrequency.map((error, index) => {
        const errorTypeId = error.errorTagCodeId ?? index + 1;
        return {
            id: errorTypeId,
            errorType: `${errorTypeId}. ${error.errorTagName}`,
            errorTagName: error.errorTagName,
            errorsReported: error.errorsReported,
            percentageOfTotalReports: error.percentageOfTotalReports,
        };
    });
    const totalErrorsReported = performanceReportData.errorBreakdownByTypeAndFrequency.reduce(
        (acc, error) => acc + error.errorsReported, 0
    );
    const ErrorTypeNoRowsOverlay = () => (
        <Box sx={{ padding: '12px 16px', color: textMuted }}>
            No Entries
        </Box>
    );
    const errorTypeRowsWithTotal = hasErrorTypeData ? [
        ...errorTypeRows,
        {
            id: 'total',
            errorType: 'Total',
            errorsReported: totalErrorsReported,
            percentageOfTotalReports: 100,
        },
    ] : [];
    const selectedMonthField = `m${reportedPeriod.month}`;
    const filingsByMonthColumns: GridColDef[] = [
        {
            field: 'metric',
            headerName: `${reportedPeriod.year}`,
            minWidth: 135,
            ...columnOptions,
        },
        ...Object.keys(Months).map((month) => {
            const monthNumber = parseInt(month);
            const monthLabel = Months[monthNumber as keyof typeof Months].slice(0, 3).toUpperCase();
            return {
                field: `m${monthNumber}`,
                headerName: monthLabel,
                minWidth: 50,
                maxWidth: 80,
                flex: 1,
                align: 'right' as const,
                headerAlign: 'right' as const,
                ...columnOptions,
                renderCell: (params: GridRenderCellParams) => {
                    if (params.row.metric === 'Error Return Rate') {
                        const value = params.value as { count?: number; rate?: number } | undefined;
                        if (!value) {
                            return '0 (0%)';
                        }
                        return `${value.count ?? 0} (${value.rate ?? 0}%)`;
                    }
                    return params.value;
                },
            };
        }),
    ];
    const filingsByMonthRows = (() => {
        const totalTransactionsRow: Record<string, number | string> = {
            id: 'totalTransactions',
            metric: 'Total Transactions',
        };
        const errorReturnRateRow: Record<string, number | string | { count: number; rate: number }> = {
            id: 'errorReturnRate',
            metric: 'Error Return Rate',
        };

        Object.keys(Months).forEach((month) => {
            const monthNumber = parseInt(month);
            const filing = performanceReportData.filingsByMonth
                .filter(f => f.year === reportedPeriod.year)
                .find(f => f.month === monthNumber);
            const monthKey = `m${monthNumber}`;

            totalTransactionsRow[monthKey] = filing?.totalTransactions || 0;
            const errorRate = filing?.totalTransactions 
                ? round((filing?.totalFilingsWithErrors / filing?.totalTransactions) * 100, 0) || 0 
                : 0;
            errorReturnRateRow[monthKey] = {
                count: filing?.totalFilingsWithErrors || 0,
                // rate: filing?.errorReturnRate || 0,
                rate: errorRate,
            };
        });

        return [totalTransactionsRow, errorReturnRateRow];
    })();

    //#region error breakdown functions
    const buildBreakdown = (items: ErrorBreakdownByStateAndPolicyTypeModel[]) => {
        const totals = items.reduce((acc, item) => {
            acc.totalErrors += item.errorsReported;
            acc.byError[item.errorTagName] = (acc.byError[item.errorTagName] || 0) + item.errorsReported;
            return acc;
        }, {
            totalErrors: 0,
            byError: {} as Record<string, number>,
        });
        
        const breakdown = Object.entries(totals.byError)
            .map(([errorTagName, errorsReported]) => ({
                errorTagName,
                errorsReported,
                percentage: totals.totalErrors ? round((errorsReported / totals.totalErrors) * 100, 0) : 0,
            }))
            .sort((a, b) => b.errorsReported - a.errorsReported);
        
        return { totalErrors: totals.totalErrors, breakdown };
    };
    
    const getFilteredErrorBreakdown = (data: ErrorBreakdownByStateAndPolicyTypeModel[], groupBy: FilterableField, filters?: FilterConfig[]): GroupedErrorBreakdown[] => {
        // Apply filters first
        let filteredData = data;
        
        if (filters && filters.length > 0) {
            filteredData = data.filter(item => {
                return filters.every(filter => {
                    switch (filter.field) {
                        case 'state':
                            return filter.value === undefined ||
                                item.stateAbbv === filter.value ||
                                item.stateId === filter.value;
                        case 'transactionType':
                            return filter.value === undefined ||
                                item.transactionType === filter.value ||
                                item.transactionTypeId === filter.value;
                        default:
                            return true;
                    };
                });
            });
        }
        
        // Group by the specified field
        const grouped = filteredData.reduce((acc, item) => {
            const groupedLabels = {
                state: {
                    key: `${item.stateId}-${item.stateAbbv}`,
                    label: item.stateAbbv,
                },
                transactionType: {
                    key: `${item.transactionTypeId}-${item.transactionType}`,
                    label: item.transactionType,
                },
                default: {
                    key: 'unknown',
                    label: 'Unknown',
                },
            };
            
            const { key, label } = groupedLabels[groupBy] || groupedLabels.default;
            
            if (!acc[key]) {
                acc[key] = {
                    groupKey: key,
                    groupLabel: label,
                    policyNumbers: new Set<string>(),
                    items: [] as ErrorBreakdownByStateAndPolicyTypeModel[],
                };
            }
            
            acc[key].policyNumbers.add(item.policyNumber);
            acc[key].items.push(item);
            return acc;
        }, {} as Record<string, {
            groupKey: string;
            groupLabel: string;
            policyNumbers: Set<string>;
            items: ErrorBreakdownByStateAndPolicyTypeModel[];
        }>);

        // Build breakdown for each group
        return Object.values(grouped).map(group => {
            const { totalErrors, breakdown } = buildBreakdown(group.items);
            return {
                groupKey: group.groupKey,
                groupLabel: group.groupLabel,
                policyCount: group.policyNumbers.size,
                totalErrors,
                breakdown,
                items: group.items,
            };
        }).sort((a, b) => b.totalErrors - a.totalErrors);
    };
    const convertToTableRows = (groups: GroupedErrorBreakdown[], sortBy: 'label' | 'errors' = 'label'): ErrorBreakdownTableRow[] => {
        const rows = groups.map((group, groupIndex) => ({
            id: `${group.groupLabel}-${groupIndex}`,
            groupLabel: group.groupLabel,
            policyCount: group.policyCount,
            totalErrors: group.totalErrors,
            breakdown: group.breakdown,
            breakdownSummary: `${group.breakdown.length} error type${group.breakdown.length === 1 ? '' : 's'}`,
        }));
        
        // Sort based on preference
        if (sortBy === 'label') {
            return rows.sort((a, b) => a.groupLabel.localeCompare(b.groupLabel));
        } else {
            return rows.sort((a, b) => b.totalErrors - a.totalErrors);
        };
    };
    const renderBreakdownTooltipContent = (breakdown: ErrorTagBreakdown[]) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '260px' }}>
            {breakdown.map((item) => (
                <Box key={item.errorTagName} sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Box sx={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '2px',
                        backgroundColor: getErrorTypeColor(item.errorTagName),
                        flexShrink: 0,
                    }} />
                    <Box sx={{ flex: 1 }}>{item.errorTagName}</Box>
                    <Box sx={{ minWidth: '70px', textAlign: 'right' }}>{item.errorsReported}</Box>
                </Box>
            ))}
        </Box>
    );
    const getErrorBreakdownColumns = (headerName: string, width: number): GridColDef[] => {
        const columns: GridColDef[] = [
            { field: 'groupLabel', headerName: headerName, width: width, ...columnOptions },
            { field: 'policyCount', headerName: 'Transactions',
                type: 'number',
                width: 140,
                align: 'right', headerAlign: 'right',
                ...columnOptions,
            },
            { field: 'totalErrors', headerName: 'Total Errors',
                type: 'number',
                width: 140,
                align: 'right', headerAlign: 'right',
                ...columnOptions,
            },
            { field: 'breakdownSummary', headerName: 'Error Breakdown',
                flex: 1, minWidth: 260,
                /*
                renderCell: (params) => (
                    <Tooltip arrow
                        title={renderBreakdownTooltipContent(params.row.breakdown)}
                        slotProps={{
                            tooltip: {
                                sx: {
                                    backgroundColor: '#fff',
                                    color: '#000',
                                    border: '1px solid #e0e0e0',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    padding: '8px 10px',
                                },
                            },
                            arrow: { sx: { color: '#fff' } },
                        }}
                    >
                        <span>{params.value}</span>
                    </Tooltip>
                ),
                */
                ...columnOptions,
            },
        ];
        return columns;
    };

    const errorBreakdownByStateTableGroups = getFilteredErrorBreakdown(
        performanceReportData.errorBreakdownByStateAndPolicyType,
        'state'
    );
    const hasErrorBreakdownByStateTableData = errorBreakdownByStateTableGroups.length > 0;
    const errorBreakdownByStateTableRows = convertToTableRows(errorBreakdownByStateTableGroups, 'label');
    const errorBreakdownByStateTableColumns = getErrorBreakdownColumns('State', 120);


    const errorBreakdownByTransactionTypeTableGroups = getFilteredErrorBreakdown(
        performanceReportData.errorBreakdownByStateAndPolicyType,
        'transactionType'
    );
    const hasErrorBreakdownByTransactionTypeTableData = errorBreakdownByTransactionTypeTableGroups.length > 0;
    const errorBreakdownByTransactionTypeTableRows = convertToTableRows(errorBreakdownByTransactionTypeTableGroups, 'label');
    const errorBreakdownByTransactionTypeTableColumns = getErrorBreakdownColumns('Transaction Type', 200);

    const getDetailPanelHeight = useCallback((params: GridRowParams) => Math.max(52, params.row.breakdown.length * 28 + 16), []);

    const dataGridHeaderSx = {
        '& .MuiDataGrid-columnHeaders': {
            backgroundColor: tableHeaderBg,
            color: textPrimary,
            fontWeight: 600,
            borderBottom: `1px solid ${borderColorFlat}`,
        },
        '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${borderColorFlat}`,
        },
    };
    const errorBreakdownDetailPanelContent = useCallback<NonNullable<DataGridProProps['getDetailPanelContent']>>(({ row }) => (
        <Box sx={{ padding: '8px 16px' }}>
            {row.breakdown.map((item: { errorTagName: string; percentage: number; errorsReported: number }) => (
                <Box key={item.errorTagName} sx={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '2px 0' }}>
                    <Box sx={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '2px',
                        backgroundColor: getErrorTypeColor(item.errorTagName),
                        flexShrink: 0,
                    }} />
                    <Box sx={{ flex: 1 }}>{item.errorTagName} - {item.errorsReported} ({item.percentage}%)</Box>
                </Box>
            ))}
        </Box>
    ), [getErrorTypeColor]);
    const errorBreakdownTableStyles = {
        border: 'none',
        '& .MuiDataGrid-columnHeaders': {
            backgroundColor: tableHeaderBg,
            color: textPrimary,
            fontWeight: 600,
            borderBottom: `1px solid ${borderColorFlat}`,
        },
        '& .MuiDataGrid-detailPanel': {
            backgroundColor: tableRowAltBg,
        },
        '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${borderColorFlat}`,
        },
    };
    //#endregion error breakdown functions
    
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ width: '100%' }}>
            <div style={{ position: "fixed", top: "0", left: "0", zIndex: "9990", width: "100%" }}>
                {loading && <LinearProgress color="info" sx={{ backgroundColor: neutralColors.darkBlueGray }} />}
            </div>
            <Box sx={{ width: '100%' }} className="customer-header">
                {/* Page header - flat bar matching site */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    px: { xs: 4, md: 2 },
                    py: 1.5,
                    borderBottom: `1px solid ${borderColorFlat}`,
                    backgroundColor: pageHeaderBg,
                    minHeight: 56,
                    marginBottom: 2,
                    marginLeft: { xs: '-24px', md: '0px' },
                    marginRight: { xs: '-24px', md: '0px' },
                }}>
                    <Typography component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600, color: textPrimary }}>
                        Monthly Account Performance Report
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2,
                        }}
                    >
                        <MonthDropdown />
                        <YearDropdown />
                        {!IsClientUser && (
                            <Grid item xl={3} lg={4} sm={6} xs={12}>
                                <PartnerDropdown />
                            </Grid>
                        )}
                        <Button
                            variant="contained"
                            size="medium"
                            onClick={() => getMonthlyAccountPerformanceReport()}
                            startIcon={<Search />}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                backgroundColor: LightBlueBtnColor,
                                '&:hover': { backgroundColor: '#176990' },
                                px: 2,
                            }}
                        >
                            Search
                        </Button>
                        <Button
                            variant="outlined"
                            size="medium"
                            onClick={() => {
                                const initial = {
                                    month: new Date().getMonth() + 1,
                                    year: new Date().getFullYear(),
                                    partnersId: IsClientUser ? user?.partnersId || 0 : 0,
                                };
                                setFormData(initial);
                                getMonthlyAccountPerformanceReport(initial);
                            }}
                            startIcon={<Close />}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                borderColor: borderColor,
                                color: textPrimary,
                                '&:hover': {
                                    borderColor: LightBlueBtnColor,
                                    backgroundColor: HoverUnselectedLightBlueBtnColor,
                                    color: textPrimary,
                                },
                                px: 2,
                            }}
                        >
                            Clear
                        </Button>
                    </Box>
                </Box>
                
                
                <Box
                    sx={{
                        borderBottom: '1px solid #e2e8f0',
                        backgroundColor: '#ffffff',
                        borderRadius: '12px',
                        mb: 2,
                        px: 3,
                        py: 2.5,
                        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
                        border: '1px solid #e2e8f0',
                    }}
                >
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#334155', mb: 0.25 }}>
                        Key metrics — {getMonthName(reportedPeriod.month)} {reportedPeriod.year}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, color: '#64748b', mb: 2 }}>
                        Summary for the selected period.
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1.5 }}>
                        
                            <Box sx={{ px: 2, py: 1, borderRadius: '8px', backgroundColor: '#f8fafc', color: '#334155', fontWeight: 600, fontSize: '0.875rem', display: 'inline-flex', alignItems: 'flex-start', gap: 0.5 }}>
                                <Tooltip title={`Total number of transactions reported during ${getMonthName(reportedPeriod.month)} ${reportedPeriod.year}`} arrow placement="top">
                                    <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#64748b', alignSelf: 'flex-start' }} />
                                </Tooltip>
                                <div className='d-inline-flex flex-column justify-content-start align-items-center' style={{ lineHeight: '15px' }}>
                                    Total Transactions Reported: 
                                <span style={{ fontSize: '12px', color: '#64748b', marginRight: 'auto'}}>(During this month)</span>
                                </div>
                                {performanceReportData.totalTransactionsReported}
                            </Box>
                            <Box sx={{ px: 2, py: 1, borderRadius: '8px', backgroundColor: '#f8fafc', color: '#334155', fontWeight: 600, fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Tooltip title={`Total number of filings processed without return during ${getMonthName(reportedPeriod.month)} ${reportedPeriod.year}`} arrow placement="top">
                                    <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#64748b', alignSelf: 'center' }} />
                                </Tooltip>
                                Filings Processed without Return: {performanceReportData.totalFilingsProcessed.filter(filing => filing.policyStatus === 'Filed').reduce((acc, filing) => acc + filing.totalFilingsProcessed, 0)}
                                {/* <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#64748b' }} /> */}
                            </Box>
                            <Box sx={{ px: 2, py: 1, borderRadius: '8px', backgroundColor: '#fef2f2', color: '#b91c1c', fontWeight: 600, fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Tooltip title={`Number of times transactions were moved to "Waiting on Client" during ${getMonthName(reportedPeriod.month)} ${reportedPeriod.year}`} arrow placement="top">
                                    <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#b91c1c', alignSelf: 'center' }} />
                                </Tooltip>
                                Return count: {performanceReportData.transactionsReturnedToClient ?? 'N/A'}
                            </Box>
                        
                            <Box sx={{ px: 2, py: 1, borderRadius: '8px', backgroundColor: '#f0f9ff', color: '#2099d2', fontWeight: 600, fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Tooltip title="Average number of days from when a filing is returned to when the client attaches documents" arrow placement="top">
                                    <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#2099d2', alignSelf: 'center' }} />
                                </Tooltip>
                                Avg# of Days to attach documents: {round(performanceReportData.avgAttachDocumentsDays, 0)}
                            </Box>

                            <Box sx={{ px: 2, py: 1, borderRadius: '8px', backgroundColor: '#f0f9ff', color: '#2099d2', fontWeight: 600, fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Tooltip title="Average number of days from when a filing is returned to when it is filed" arrow placement="top">
                                    <InfoOutlinedIcon sx={{ fontSize: '14px', color: '#2099d2', alignSelf: 'center' }} />
                                </Tooltip>
                                Avg# of Days to respond to Returned Filings: {round(performanceReportData.clientResponseTimes.avgResponseTimeDays, 0)}
                            </Box>
                    </Box>
                </Box>
                </Box>

                {/* summary + timeliness in one row to fill top space */}
                <Grid container spacing={2}  >
                    {/* <Grid item xs={12} md={6} >
                        <TableContainer component={Paper} elevation={0} sx={{ ...TableContainerStyle(null), width: '100%' }}>
                            <Table size="small" sx={TableStyle}>
                                <TableBody>
                                    <Tooltip title={`Total number of transactions reported during ${getMonthName(reportedPeriod.month)} ${reportedPeriod.year}`} arrow>
                                        <TableRow>
                                            <TableCell sx={TableCellDescriptionStyle}>Total Transactions Reported during this month:</TableCell>
                                            <TableCell align="right" sx={TableCellValueStyle}>{performanceReportData.totalTransactionsReported}</TableCell>
                                        </TableRow>
                                    </Tooltip>
                                    <Tooltip title={`Total number of filings processed without return during ${getMonthName(reportedPeriod.month)} ${reportedPeriod.year}`} arrow>
                                        <TableRow>
                                            <TableCell sx={TableCellDescriptionStyle}># of Filings Processed without Return:</TableCell>
                                            <TableCell align="right" sx={TableCellValueStyle}>{performanceReportData.totalFilingsProcessed.filter(filing => filing.policyStatus === 'Filed').reduce((acc, filing) => acc + filing.totalFilingsProcessed, 0)}</TableCell>
                                        </TableRow>
                                    </Tooltip>
                                    <Tooltip title={`Total number of filings returned with errors during ${getMonthName(reportedPeriod.month)} ${reportedPeriod.year}`} arrow>
                                        <TableRow>
                                            <TableCell sx={{ ...TableCellDescriptionStyle, color: RedColor }}>Error Return Rate:</TableCell>
                                            <TableCell align="right" sx={{ ...TableCellValueStyle, color: RedColor }}>{performanceReportData.totalFilingsReturnedWithErrors}</TableCell>
                                        </TableRow>
                                    </Tooltip>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid> */}
                    {/* <Grid item xs={12} md={6} sx={{ flex: '1 1 280px', minWidth: 0 }}>
                        <TableContainer component={Paper} elevation={0} sx={{ ...TableContainerStyle(null), width: '100%' }}>
                            <Table size="small" sx={TableStyle}>
                                <TableBody>
                                    <Tooltip title="Average turnaround time from status 'Waiting on Client' to 'Filed'" arrow>
                                        <TableRow>
                                            <TableCell sx={TableCellDescriptionStyle}>Avg# of Days to File:</TableCell>
                                            <TableCell align="right" sx={TableCellValueStyle}>{round(performanceReportData.timelinessOfDocumentSubmission.avgTurnaroundDays, 0)}</TableCell>
                                        </TableRow>
                                    </Tooltip>
                                    <Tooltip title="Average number of days it takes a client to reply to a message sent by an MISC user" arrow>
                                        <TableRow>
                                            <TableCell sx={TableCellDescriptionStyle}>Avg# of Days to respond to MISC message:</TableCell>
                                            <TableCell align="right" sx={TableCellValueStyle}>{round(performanceReportData.clientResponseTimes.avgResponseTimeDays, 0)}</TableCell>
                                        </TableRow>
                                    </Tooltip>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid> */}
                </Grid>

                {/* main layout - equal columns, full width */}
                <Grid container spacing={2} sx={{ mt: 2 }}>
                    {/* left column */}
                    <Grid item sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: '1 1 0%' }}>
                        {/* filings sorted by error type */}
                        <Paper elevation={0} sx={{ ...TableContainerStyle(), width: '100%', minWidth: 0, border: `1px solid ${borderColorFlat}` }}>
                            <Box sx={BoxTitleStyle}>Filings returned with errors by Error Type — {getMonthName(reportedPeriod.month)} {reportedPeriod.year}</Box>
                            <DataGridPro rows={errorTypeRowsWithTotal} columns={errorTypeColumns}
                                autoHeight
                                disableSelectionOnClick
                                disableChildrenSorting
                                hideFooter
                                rowHeight={36}
                                disableColumnMenu
                                disableColumnReorder
                                disableColumnFilter
                                disableColumnSelector
                                disableColumnPinning
                                components={{ NoRowsOverlay: ErrorTypeNoRowsOverlay }}
                                getRowClassName={(params) =>
                                    params.row?.id === 'total' ? 'error-type-total-row' : ''
                                }
                                sx={{
                                    border: 'none',
                                    ...dataGridHeaderSx,
                                    '& .error-type-total-row .MuiDataGrid-cell': {
                                        fontWeight: 600,
                                    },
                                }}
                            />
                        </Paper>
                    </Grid>

                    {/* right column */}
                    <Grid item sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: '1 1 0%' }}>
                        {/* Filings by Month */}
                        <Paper elevation={0} sx={{ ...TableContainerStyle(), width: '100%', minWidth: 0, border: `1px solid ${borderColorFlat}` }}>
                            <DataGridPro rows={filingsByMonthRows} columns={filingsByMonthColumns}
                                autoHeight
                                disableSelectionOnClick
                                hideFooter
                                rowHeight={36}
                                disableColumnMenu
                                disableColumnReorder
                                disableColumnFilter
                                disableColumnSelector
                                disableColumnPinning
                                getCellClassName={(params) => {
                                    if (params.field === selectedMonthField && params.row?.metric) {
                                        return 'selected-month-metric-cell';
                                    }
                                    return '';
                                }}
                                sx={{
                                    border: 'none',
                                    ...dataGridHeaderSx,
                                    '& .selected-month-metric-cell': {
                                        fontWeight: 600,
                                    },
                                }}
                            />
                        </Paper>
                    </Grid>
                </Grid>

                {/* Error Breakdown by State / Transaction Type */}
                <Grid container spacing={2} sx={{ mt: 2 }}>
                    {/* left column - Error Breakdown by State */}
                    <Grid item xs={12} lg={6} >
                        <Paper elevation={0} sx={{ ...TableContainerStyle(), width: '100%', minWidth: 0, border: `1px solid ${borderColorFlat}` }}>
                            <Box sx={BoxTitleStyle}>Error Breakdown by State</Box>
                            {hasErrorBreakdownByStateTableData ? (
                                <DataGridPro rows={errorBreakdownByStateTableRows} 
                                    columns={errorBreakdownByStateTableColumns} 
                                    autoHeight
                                    disableSelectionOnClick
                                    hideFooter
                                    rowHeight={32}
                                    disableColumnMenu
                                    disableColumnReorder
                                    disableColumnFilter
                                    disableColumnSelector
                                    disableColumnPinning
                                    getDetailPanelHeight={getDetailPanelHeight}
                                    getDetailPanelContent={errorBreakdownDetailPanelContent}
                                    sx={errorBreakdownTableStyles}
                                />
                            ) : (
                                <Box sx={{
                                    width: '100%',
                                    padding: '16px 12px',
                                    color: textMuted,
                                }}>No Entries</Box>
                            )}
                        </Paper>
                    </Grid>
                    {/* right column - Error Breakdown by Transaction Type */}
                    <Grid item xs={12} lg={6} >
                        <Paper elevation={0} sx={{ ...TableContainerStyle(), width: '100%', minWidth: 0, border: `1px solid ${borderColorFlat}` }}>
                            <Box sx={BoxTitleStyle}>Error Breakdown by Transaction Type</Box>
                            {hasErrorBreakdownByTransactionTypeTableData ? (
                                <DataGridPro rows={errorBreakdownByTransactionTypeTableRows} 
                                    columns={errorBreakdownByTransactionTypeTableColumns} 
                                    autoHeight
                                    disableSelectionOnClick
                                    hideFooter
                                    rowHeight={32}
                                    disableColumnMenu
                                    disableColumnReorder
                                    disableColumnFilter
                                    disableColumnSelector
                                    disableColumnPinning
                                    getDetailPanelHeight={getDetailPanelHeight}
                                    getDetailPanelContent={errorBreakdownDetailPanelContent}
                                    sx={errorBreakdownTableStyles}
                                />
                            ) : (
                                <Box sx={{
                                    width: '100%',
                                    padding: '16px 12px',
                                    color: textMuted,
                                }}>No Entries</Box>
                            )}
                        </Paper>
                    </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ my: 2 }}>
                    {/* pie chart of filings returned with errors by error type */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: '8px',
                                border: `1px solid ${borderColorFlat}`,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                backgroundColor: WhiteColor,
                                width: '100%',
                            }}
                        >
                            <Typography component="h3" sx={{ fontSize: '0.9375rem', fontWeight: 600, color: textPrimary, marginBottom: 1 }}>By Error Type</Typography>
                            {hasErrorTypeData ? (
                                <PieChart width={600} height={400}
                                    series={[
                                        {
                                            data: getPieChartData(),
                                            arcLabel: (item) => item.id?.toString() || '0', // No labels on slices; label shown in tooltip on hover
                                            innerRadius: 0,
                                            outerRadius: '95%',
                                            cornerRadius: 4,
                                            paddingAngle: 2,
                                        }
                                    ]}
                                    margin={{ left: 100 }}
                                    slotProps={{
                                        legend: { hidden: true },
                                    }}
                                    sx={{
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        '& .MuiChartsLegend-mark': { rx: 2 },
                                        '& .MuiChartsLegend-label': { fontSize: '0.8125rem' },
                                    }}
                                />
                            ) : (
                                <Box sx={{
                                    width: 600,
                                    height: 400,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: textMuted,
                                }}>No Entries</Box>
                            )}
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {/* bar chart of filings by month */}
                        <Box sx={{ width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <Paper elevation={0}
                                sx={{
                                    p: 2,
                                    borderRadius: '8px',
                                    border: `1px solid ${borderColorFlat}`,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    backgroundColor: WhiteColor,
                                    width: '100%',
                                    height: '100%',
                                }}
                            >
                                {/* Stacked bar chart of filings by month, error return rate stacked on top of total transactions, x-axis is the month, y-axis is the number of filings */}
                                {hasFilingsByMonthData ? (
                                    <BarChart width={600} height={400}
                                        dataset={chartData}
                                        xAxis={[
                                            { scaleType: 'band', dataKey: 'month', label: `${reportedPeriod.year}` },
                                        ]}
                                        yAxis={[
                                            { scaleType: 'linear', min: 0, max: 100, label: '' },
                                        ]}
                                        series={[
                                            { 
                                                // id: 'nonErrorPercent',
                                                // dataKey: 'nonErrorPercent', 
                                                id: 'totalTransactions',
                                                dataKey: 'totalTransactions',
                                                label: 'Total Transactions', 
                                                stack: 'filings', 
                                                color: brandColors.royalBlue, 
                                            },
                                            { 
                                                id: 'errorPercent',
                                                dataKey: 'errorPercent', 
                                                label: 'Error Return Rate', 
                                                stack: 'filings', 
                                                color: brandColors.orangeCustom, 
                                                valueFormatter: (value) => {
                                                    return `${value}%`;
                                                },
                                            },
                                        ]}
                                        margin={{ left: 70 }}
                                        sx={{
                                            '& .MuiChartsLegend-series': { marginBottom: '16px' },
                                            '& .MuiChartsLegend-series:last-of-type': { marginBottom: 0 },
                                            '& .MuiChartsLegend-mark': { rx: 2 },
                                            '& .MuiChartsLegend-label': { fontSize: '0.8125rem' },
                                            '& .MuiChartsAxis-left .MuiChartsAxis-label': {
                                                transform: 'translate(-16px, 0)',
                                            },
                                            '& .MuiChartsAxis-left .MuiChartsAxis-tickLabel': {
                                                display: 'none',
                                            },
                                            '& .MuiChartsAxis-left .MuiChartsAxis-tick': {
                                                display: 'none',
                                            },
                                            '& .MuiChartsAxis-left .MuiChartsAxis-line': {
                                                display: 'none',
                                            },
                                            // '& .MuiChartsAxis-bottom .MuiChartsAxis-tickLabel': {
                                            //     display: 'none',
                                            // },
                                            '& .MuiChartsAxis-bottom .MuiChartsAxis-tick': {
                                                display: 'none',
                                            },
                                            // '& .MuiChartsAxis-bottom .MuiChartsAxis-line': {
                                            //     display: 'none',
                                            // },
                                        }}
                                    />
                                ) : (
                                    <Box sx={{
                                        width: 600,
                                        height: 400,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: textMuted,
                                    }}>No Entries</Box>
                                )}
                            </Paper>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </LocalizationProvider>
    );
};